import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Constants } from '../../constants';
import type { ConvocationRowItem } from '../../components/dashboard/dashboard-mister/mister-convocation-shared';

/** Payload allineato a wisper-api `convocation.validation.json` / `Convocation.create`. */
export type ConvocationApiPayload = {
    title: string;
    description: string;
    status: string;
    userId: string;
    categoryId: string;
    date: string;
    location: string;
    address: string;
    city: string;
    province: string;
    linkPosition: string;
    users: string[];
};

@Injectable({
    providedIn: 'root',
})
export class ConvocationService {
    constructor(private http: HttpClient) {}

    private getRequestHeaders(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization:
                localStorage.getItem(Constants.LOCAL_TOKEN) ??
                environment.publicApiToken,
        });
    }

    /**
     * `GET /convocations` — la API restituisce `findAndCountAll` in `data.rows` / `data.count`.
     */
    async listConvocations(params?: Record<string, string | number>): Promise<any> {
        try {
            return await this.http
                .get(environment.apiUrl + '/convocations', {
                    headers: this.getRequestHeaders(),
                    params: params as any,
                })
                .toPromise();
        } catch (e) {
            console.error('error listConvocations: ', e);
            return { success: false };
        }
    }

    async getConvocationById(id: string): Promise<any> {
        try {
            return await this.http
                .get(environment.apiUrl + `/convocations/${id}`, {
                    headers: this.getRequestHeaders(),
                })
                .toPromise();
        } catch (e) {
            console.error('error getConvocationById: ', e);
            return { success: false };
        }
    }

    private static httpErrorMessage(err: unknown): string {
        if (!(err instanceof HttpErrorResponse)) {
            return '';
        }
        const e = err.error;
        if (typeof e === 'string' && e.trim()) {
            return e.trim().slice(0, 500);
        }
        if (e && typeof e === 'object') {
            const o = e as Record<string, unknown>;
            const msg = o['message'] ?? o['error'] ?? o['code'];
            if (typeof msg === 'string') {
                return msg;
            }
        }
        return err.message || `HTTP ${err.status}`;
    }

    async createConvocation(body: ConvocationApiPayload): Promise<any> {
        try {
            return await this.http
                .post(environment.apiUrl + '/convocations', body, {
                    headers: this.getRequestHeaders(),
                })
                .toPromise();
        } catch (e) {
            console.error('error createConvocation: ', e);
            return {
                success: false,
                errorMessage: ConvocationService.httpErrorMessage(e),
            };
        }
    }

    async updateConvocation(id: string, body: Partial<ConvocationApiPayload>): Promise<any> {
        try {
            return await this.http
                .put(environment.apiUrl + `/convocations/${id}`, body, {
                    headers: this.getRequestHeaders(),
                })
                .toPromise();
        } catch (e) {
            console.error('error updateConvocation: ', e);
            return {
                success: false,
                errorMessage: ConvocationService.httpErrorMessage(e),
            };
        }
    }

    /** Normalizza risposta lista (rows + count o array). */
    extractRows(response: any): any[] {
        const d = response?.data;
        if (!d) {
            return [];
        }
        if (Array.isArray(d)) {
            return d;
        }
        if (Array.isArray(d.rows)) {
            return d.rows;
        }
        return [];
    }

    buildApiPayloadFromRow(
        row: ConvocationRowItem,
        userId: string,
        statusOverride?: string,
    ): ConvocationApiPayload {
        const date =
            row.date && !Number.isNaN(new Date(row.date).getTime())
                ? new Date(row.date).toISOString()
                : new Date().toISOString();
        return {
            title: (row.title ?? '').trim(),
            description: (row.description ?? '').trim() || '—',
            status: (statusOverride ?? row.status ?? 'Bozza').trim(),
            userId,
            categoryId: (row.categoryId ?? '').trim(),
            date,
            location: (row.location ?? '').trim(),
            address: (row.address ?? '').trim(),
            city: (row.city ?? '').trim(),
            province: (row.province ?? '').trim() || '—',
            linkPosition: (row.linkPosition ?? '').trim() || '—',
            users: row.users ?? [],
        };
    }

    mapApiRecordToRow(
        raw: any,
        categoryLabelFallback?: string,
    ): ConvocationRowItem {
        const users = raw?.users;
        const invitedCount = Array.isArray(users) ? users.length : 0;
        let dateVal = raw?.date;
        if (dateVal != null && typeof dateVal !== 'string') {
            dateVal = new Date(dateVal).toISOString();
        }
        return {
            id: raw.id,
            title: raw.title ?? '',
            description: raw.description ?? '',
            status: raw.status ?? '',
            date: (dateVal as string) ?? '',
            location: raw.location ?? '',
            address: raw.address ?? '',
            city: raw.city ?? '',
            province: raw.province ?? '',
            linkPosition: raw.linkPosition ?? '',
            categoryId: raw.categoryId,
            categoryName: raw.category?.name ?? categoryLabelFallback,
            invitedCount,
            users: Array.isArray(users) ? [...users] : undefined,
            userId: raw.userId,
        };
    }
}
